import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import UserCard from '../user/UserCard';
import CommunityCard from '../communities/CommunityCard';
import useAuth from '../../hooks/useAuth';
import { getUsers } from '../../api/users';
import { getCommunities } from '../../api/communities';

/**
 * RightBar component with suggestions and trends
 * @returns {JSX.Element} RightBar component
 */
const RightBar = () => {
  const { isAuth } = useAuth();
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [trendingCommunities, setTrendingCommunities] = useState([]);
  const [loading, setLoading] = useState({
    users: false,
    communities: false,
  });
  
  // Fetch suggested users and trending communities
  useEffect(() => {
    if (isAuth) {
      fetchSuggestedUsers();
      fetchTrendingCommunities();
    }
  }, [isAuth]);
  
  // Fetch suggested users
  const fetchSuggestedUsers = async () => {
    try {
      setLoading((prev) => ({ ...prev, users: true }));
      const response = await getUsers({
        page: 1,
        limit: 3,
        sort: '-createdAt',
      });
      setSuggestedUsers(response.data.data.users);
    } catch (error) {
      console.error('Error fetching suggested users:', error);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };
  
  // Fetch trending communities
  const fetchTrendingCommunities = async () => {
    try {
      setLoading((prev) => ({ ...prev, communities: true }));
      const response = await getCommunities({
        page: 1,
        limit: 3,
        sort: '-memberCount',
      });
      setTrendingCommunities(response.data.data.communities);
    } catch (error) {
      console.error('Error fetching trending communities:', error);
    } finally {
      setLoading((prev) => ({ ...prev, communities: false }));
    }
  };
  
  return (
    <div className="hidden lg:block py-4 pl-4 overflow-y-auto sticky top-16 h-[calc(100vh-4rem)]">
      <div className="w-64 space-y-6" style={{width: "100%"}}>
        {/* Search box */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <svg
                className="h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Trending topics */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">Trending Topics</h2>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Link
                to="/search?q=react"
                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium hover:bg-blue-200"
              >
                #React
              </Link>
              <Link
                to="/search?q=javascript"
                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium hover:bg-blue-200"
              >
                #JavaScript
              </Link>
              <Link
                to="/search?q=webdev"
                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium hover:bg-blue-200"
              >
                #WebDev
              </Link>
              <Link
                to="/search?q=design"
                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium hover:bg-blue-200"
              >
                #Design
              </Link>
              <Link
                to="/search?q=ai"
                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium hover:bg-blue-200"
              >
                #AI
              </Link>
            </div>
          </div>
        </div>
        
        {/* Who to follow */}
        {isAuth && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-4">Who to follow</h2>
            <div className="space-y-4">
              {loading.users ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                </div>
              ) : suggestedUsers.length > 0 ? (
                suggestedUsers.map((user) => (
                  <UserCard key={user._id} user={user} showFollowButton />
                ))
              ) : (
                <p className="text-gray-500 text-sm">No suggestions available</p>
              )}
              
              <Link
                to="/explore"
                className="text-blue-600 text-sm hover:underline block text-center mt-2"
              >
                Show more
              </Link>
            </div>
          </div>
        )}
        
        {/* Popular communities */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-4">Popular Communities</h2>
          <div className="space-y-4">
            {loading.communities ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
              </div>
            ) : trendingCommunities.length > 0 ? (
              trendingCommunities.map((community) => (
                <CommunityCard key={community._id} community={community} />
              ))
            ) : (
              <p className="text-gray-500 text-sm">No communities found</p>
            )}
            
            <Link
              to="/communities/discover"
              className="text-blue-600 text-sm hover:underline block text-center mt-2"
            >
              View all communities
            </Link>
          </div>
        </div>
        
        {/* Footer links */}
        <div className="text-gray-500 text-xs">
          <div className="flex flex-wrap gap-2">
            <Link to="/about" className="hover:underline">
              About
            </Link>
            <Link to="/terms" className="hover:underline">
              Terms
            </Link>
            <Link to="/privacy" className="hover:underline">
              Privacy
            </Link>
            <Link to="/help" className="hover:underline">
              Help Center
            </Link>
          </div>
          <p className="mt-2">© 2025 Vernet-Zung</p>
        </div>
      </div>
    </div>
  );
};

export default RightBar;